import type { ElementType, ReactNode, Ref } from 'react';

// eslint-disable-next-line no-restricted-imports
import type { ButtonProps as MuiButtonProps } from '@mui/material/Button';
// eslint-disable-next-line no-restricted-imports
import MuiButton from '@mui/material/Button';

import type { Sx } from '@/types/helpers';
import { normalizeSx } from '@/utils/normalizeSx';

export type ButtonSize = 'xsmall' | 'small' | 'normal' | 'medium' | 'large';

type ButtonSizeConfig = {
  height: number;
  icon: number;
  fontSize: number;
  spacing: number;
};

type ButtonSx = Exclude<Sx, ReadonlyArray<unknown>>;

const buttonSizeConfig: Record<ButtonSize, ButtonSizeConfig> = {
  xsmall: { height: 22, icon: 16, fontSize: 12, spacing: 0.5 },
  small: { height: 30, icon: 16, fontSize: 14, spacing: 0.75 },
  normal: { height: 40, icon: 18, fontSize: 16, spacing: 0.75 },
  medium: { height: 44, icon: 14, fontSize: 14, spacing: 0.75 },
  large: { height: 48, icon: 24, fontSize: 16, spacing: 1 }
};

const getButtonSx = (size: ButtonSize, variant: MuiButtonProps['variant']): ButtonSx => {
  const cfg = buttonSizeConfig[size];

  return theme => ({
    ...(variant !== 'text' && { height: theme.typography.pxToRem(cfg.height) }),
    px: '12px',
    lineHeight: 1.2,
    minWidth: 0,
    fontSize: theme.typography.pxToRem(cfg.fontSize),
    '& .MuiButton-startIcon, & .MuiButton-endIcon': {
      fontSize: theme.typography.pxToRem(cfg.icon),
      svg: { fontSize: theme.typography.pxToRem(cfg.icon), m: 0 }
    },
    '& .MuiButton-startIcon': { ml: 0, mr: cfg.spacing },
    '& .MuiButton-endIcon': { ml: cfg.spacing, mr: 0 }
  });
};

const buttonSxBySizeAndVariant: Record<ButtonSize, Partial<Record<string, ButtonSx>>> = {
  xsmall: {},
  small: {},
  normal: {},
  medium: {},
  large: {}
};

const getCachedButtonSx = (size: ButtonSize, variant: MuiButtonProps['variant']): ButtonSx => {
  const variantKey = variant ?? 'contained';

  buttonSxBySizeAndVariant[size][variantKey] ??= getButtonSx(size, variant);

  return buttonSxBySizeAndVariant[size][variantKey];
};

// Extend MUI Button props but replace "size" with our custom variant and
// explicitly add anchor-specific props to support component="a" use cases.
// This keeps the wrapper ergonomic while avoiding MUI's polymorphic generic complexity.
export type ButtonProps = Omit<MuiButtonProps, 'size'> & {
  children: ReactNode;
  size?: ButtonSize; // xsmall=22, small=33, large=48
  // Component prop for polymorphic behavior
  component?: ElementType;
  // Anchor-specific props for component="a" use cases
  href?: string;
  target?: string;
  download?: boolean | string;
  rel?: string;
  to?: string;
  replace?: boolean;
};

export const Button = ({
  children,
  sx,
  size = 'normal',
  variant = 'contained',
  component,
  ref,
  ...rest
}: ButtonProps & { ref?: Ref<HTMLButtonElement | HTMLAnchorElement> }) => {
  const buttonSx = getCachedButtonSx(size, variant);
  const sxArray = normalizeSx(sx);

  return (
    <MuiButton
      ref={ref}
      {...(component && { component })}
      sx={[buttonSx, ...sxArray]}
      variant={variant}
      {...rest}
    >
      {children}
    </MuiButton>
  );
};

Button.displayName = 'Button';

export default Button;
